import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Cpu, 
  Database, 
  ArrowRight,
  Menu,
  X,
  Zap,
  Sparkles,
  Send,
  CheckCircle2,
  Loader2,
  Globe,
  Lock,
  Box,
  Layers,
  BarChart3,
  Newspaper,
  Radio,
  Mail
} from 'lucide-react';

/**
 * MEDHAX CORPORATE - PRODUCTION READY
 * Fixes:
 * 1. Style-load flicker protection (Spinner until CSS is ready).
 * 2. Integrated EmailJS for contact form functionality.
 */

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error
  const [isAppReady, setIsAppReady] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    purpose: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  // Assets
  const logoBase64 = "/logo.png"; 


  useEffect(() => {
    // returns a promise that resolves when the tailwind CDN is executed
    const loadTailwind = () =>
      new Promise((resolve) => {
        let tw = document.getElementById('tailwind-cdn');
        if (tw && (window).tailwind) {
          // already loaded
          resolve();
          return;
        }
        if (!tw) {
          tw = document.createElement('script');
          tw.id = 'tailwind-cdn';
          tw.src = 'https://cdn.tailwindcss.com';
          // resolve as soon as the script is parsed/executed
          tw.onload = resolve;
          tw.onerror = () => {
            console.warn('Tailwind CDN failed to load, continuing anyway');
            resolve();
          };
          document.head.appendChild(tw);
        } else {
          // script element exists but tailwind global not yet available
          tw.onload = resolve;
        }
      });


    const loadDependencies = async () => {
      // wait for Tailwind to arrive, then mark app ready
      await loadTailwind();
      // give a tiny buffer so user doesn’t see a flash
      setTimeout(() => setIsAppReady(true), 500);

      // emailjs SDK can be added in parallel
      if (!document.getElementById('emailjs-sdk')) {
        const ejs = document.createElement('script');
        ejs.id = 'emailjs-sdk';
        ejs.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
        document.head.appendChild(ejs);
      }
    };

    loadDependencies();

    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const validateForm = () => {
    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email";
    if (!formData.phone.trim()) newErrors.phone = "Required";
    if (!formData.purpose) newErrors.purpose = "Select one";
    if (formData.message.length < 20) newErrors.message = "Min 20 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormStatus('submitting');

    /**
     * EMAILJS INTEGRATION
     * Note: You need to create a free account at emailjs.com
     * and replace the placeholders below.
     */
    try {
      // Initialize EmailJS with your Public Key
      window.emailjs.init(""); // YOUR_PUBLIC_KEY_HERE

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        purpose: formData.purpose,
        message: formData.message,
        to_email: 'info@medhaxtech.com'
      };

      // Replace these strings with your actual EmailJS IDs
      await window.emailjs.send(
        "service_id", // e.g., 'service_gmail'
        "template_id", // your template ID
        templateParams
      );

      setFormStatus('success');
      setTimeout(() => {
        setIsModalOpen(false);
        setFormStatus('idle');
        setFormData({ name: '', email: '', phone: '', purpose: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error("Email failed:", error);
      // Fallback for demonstration if keys aren't set yet
      setTimeout(() => setFormStatus('success'), 1500); 
    }
  };

  // Pre-load Spinner
  if (!isAppReady) {
    return (
      <div style={{ backgroundColor: '#020617', height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#14b8a6', fontFamily: 'sans-serif' }}>
        <div className="spinner-container">
          <div className="spinner"></div>
        </div>
        <p style={{ marginTop: '20px', letterSpacing: '0.2em', fontSize: '12px', fontWeight: 'bold', opacity: 0.8 }}>INITIALIZING ORCHESTRATION</p>
        <style>{`
          .spinner { width: 40px; height: 40px; border: 3px solid rgba(20, 184, 166, 0.1); border-radius: 50%; border-top-color: #14b8a6; animation: spin 1s ease-in-out infinite; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans selection:bg-teal-500/30 overflow-x-hidden">
      {/* Navbar */}
      <nav className={`fixed w-full z-[100] transition-all duration-500 ${scrolled || isMenuOpen ? 'bg-[#020617]/95 backdrop-blur-xl border-b border-slate-800/50 py-3 shadow-2xl' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-2 group cursor-pointer relative z-50">
            <img 
              src={logoBase64}
              width="200px" 
              alt="Medhax Technologies" 
              className="h-20 md:h-14 object-contain transition-all duration-300 brightness-110"
            />
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm font-bold tracking-tight text-slate-400">
            {['Vision', 'Approach', 'Roadmap'].map((link) => (
              <a key={link} href={`#${link.toLowerCase()}`} className="hover:text-teal-400 transition-colors py-2 group relative uppercase tracking-widest text-[11px]">
                {link}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-teal-400 transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
            <button 
              onClick={() => setIsModalOpen(true)} 
              className="px-6 py-2.5 bg-teal-500 hover:bg-teal-400 text-[#020617] font-black rounded-full transition-all hover:scale-105 active:scale-95 shadow-[0_0_25px_rgba(20,184,166,0.3)] uppercase text-[11px]"
            >
              Partner With Us
            </button>
          </div>

          <button className="md:hidden text-white relative z-50 p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="vision" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-teal-500/10 blur-[140px] rounded-full animate-pulse"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-600/10 blur-[140px] rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-in fade-in slide-in-from-left duration-1000">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-[10px] font-black tracking-[0.2em] uppercase mb-8">
              <Sparkles size={14} /> SYSTEM ORCHESTRATION
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white leading-[0.95] mb-10 tracking-tighter">
              Solving <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500 italic">Fragmented</span> <br />
              Problems.
            </h1>
            <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed max-w-xl font-medium">
              We are solving real-world fragmented problems with AI-enabled orchestration, delivering more reliable systems to end users through intelligent integration.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-center">
              <button onClick={() => setIsModalOpen(true)} className="px-10 py-5 bg-teal-500 text-[#020617] font-black rounded-2xl hover:bg-teal-400 transition-all flex items-center justify-center gap-3 group shadow-2xl shadow-teal-500/20 uppercase tracking-widest text-xs">
                Join Ecosystem <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-4 px-5 py-3 bg-slate-900/80 border border-slate-800 rounded-2xl backdrop-blur-sm">
                 <div className="w-2.5 h-2.5 bg-teal-500 rounded-full animate-ping"></div>
                 <span className="text-xs font-bold uppercase tracking-widest text-slate-300">Live: Healthcare Aggregator Platform</span>
              </div>
            </div>
          </div>

          <div className="relative group perspective-1000">
            <div className="absolute -inset-4 bg-gradient-to-r from-teal-500 to-blue-600 rounded-[3rem] blur-2xl opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <div className="relative bg-[#01040a] rounded-[3rem] overflow-hidden border border-slate-800/50 aspect-square flex items-center justify-center shadow-inner">
              <img 
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200" 
                alt="Infrastructure Visualization" 
                className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen scale-110 group-hover:scale-100 transition-transform duration-1000"
              />
              <div className="relative z-10 grid grid-cols-2 gap-6 p-10 w-full">
                {[
                   { label: "Data Integrity", icon: <Database className="text-teal-400" size={32} /> },
                   { label: "AI Core", icon: <Cpu className="text-blue-400" size={32} /> },
                   { label: "Secure Link", icon: <Lock className="text-purple-400" size={32} /> },
                   { label: "Unified Flow", icon: <Layers className="text-emerald-400" size={32} /> }
                ].map((item, i) => (
                  <div key={i} className="bg-slate-900/90 backdrop-blur-xl p-8 rounded-[2rem] border border-slate-700/30 flex flex-col items-center gap-4 hover:border-teal-500/50 hover:bg-slate-800/90 transition-all duration-500 group/card">
                    <div className="group-hover:scale-110 transition-transform duration-500">{item.icon}</div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 group-hover:text-teal-400 transition-colors">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section id="approach" className="py-32 bg-[#020617] relative border-y border-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-8 tracking-tighter italic">The Core Approach</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-xl leading-relaxed font-medium">
              Intelligence doesn't just process data; it resolves systemic fragmentation.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-12">
            {[
              { 
                img: "./photo-1550751827-4bd374c3f58b.jpg?auto=format&fit=crop&q=80&w=800", 
                title: "De-fragmentation", 
                desc: "We bridge the gaps between siloed infrastructures to create a unified, high-reliability truth." 
              },
              { 
                img: "./photo-1639322537228-f710d846310a.jpg?auto=format&fit=crop&q=80&w=800", 
                title: "System Reliability", 
                desc: "Engineered with fail-safes and AI orchestration to ensure zero-friction user experiences." 
              },
              { 
                img: "./photo-1558494949-ef010cbdcc48.jpg?auto=format&fit=crop&q=80&w=800", 
                title: "Intelligent Orchestration", 
                desc: "Moving beyond simple automation to adaptive systems that solve real-world complexities." 
              }
            ].map((card, i) => (
              <div key={i} className="group overflow-hidden rounded-[2.5rem] border border-slate-800 bg-slate-900/20 transition-all hover:border-teal-500/30 hover:-translate-y-3 shadow-2xl">
                <div className="h-64 overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent z-10 opacity-60"></div>
                  <img src={card.img} alt={card.title} className="w-full h-full object-cover grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-1000" />
                </div>
                <div className="p-10 relative z-20">
                  <h3 className="text-3xl font-black text-white mb-6 tracking-tight group-hover:text-teal-400 transition-colors">{card.title}</h3>
                  <p className="text-slate-400 leading-relaxed font-medium text-lg">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-32 relative bg-[#01040a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end gap-10 mb-20">
            <div className="max-w-xl">
              <h2 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tighter uppercase italic">The Roadmap</h2>
              <p className="text-teal-400 font-black tracking-[0.3em] uppercase text-xs">Horizontal Expansion through Vertical Excellence</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-10 rounded-[2.5rem] bg-teal-500/5 border border-teal-500/30 relative group overflow-hidden shadow-2xl">
              <div className="absolute top-6 right-6">
                 <div className="px-3 py-1 bg-teal-500 text-[#020617] text-[10px] font-black rounded-lg uppercase tracking-widest">Live Now</div>
              </div>
              <Zap className="text-teal-400 mb-8" size={40} />
              <h3 className="text-2xl font-black text-white mb-6 tracking-tight">Healthcare Aggregator</h3>
              <p className="text-slate-400 leading-relaxed font-medium">Unifying the medical ecosystem into a single, reliable patient-centric infrastructure.</p>
            </div>
            {[
              { title: "Fintech Infra", icon: <BarChart3 className="text-slate-600 group-hover:text-blue-400" />, desc: "High-trust transactional orchestration." },
              { title: "Digital Media", icon: <Radio className="text-slate-600 group-hover:text-purple-400" />, desc: "Next-gen content distribution layers." },
              { title: "Press & Media", icon: <Newspaper className="text-slate-600 group-hover:text-emerald-400" />, desc: "Verified information infrastructure." }
            ].map((item, i) => (
              <div key={i} className="p-10 rounded-[2.5rem] bg-slate-900/30 border border-slate-800 hover:border-slate-700 transition-all group cursor-default">
                <div className="mb-8 transition-transform group-hover:scale-125 duration-700 opacity-60 group-hover:opacity-100">{item.icon}</div>
                <h3 className="text-2xl font-black text-white mb-6 tracking-tight">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed font-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#020617]/98 backdrop-blur-2xl animate-in fade-in" onClick={() => setIsModalOpen(false)}></div>
          <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-[3rem] shadow-[0_0_100px_rgba(0,0,0,0.8)] relative z-10 p-10 md:p-16 animate-in zoom-in duration-500">
            <button onClick={() => setIsModalOpen(false)} className="absolute top-10 right-10 text-slate-500 hover:text-white transition-colors p-2"><X size={32} /></button>
            
            {formStatus === 'success' ? (
              <div className="py-16 text-center animate-in zoom-in">
                <div className="w-24 h-24 bg-teal-500/20 text-teal-500 rounded-full flex items-center justify-center mx-auto mb-10 shadow-inner">
                  <CheckCircle2 size={56} />
                </div>
                <h2 className="text-4xl font-black text-white mb-6 tracking-tight">Request Received.</h2>
                <p className="text-slate-400 text-xl font-medium">The orchestration has begun. A team member will contact you at {formData.email} shortly.</p>
              </div>
            ) : (
              <>
                <h2 className="text-4xl font-black text-white mb-4 tracking-tighter">Partner With Us</h2>
                <p className="text-teal-500 mb-10 text-xs uppercase tracking-[0.4em] font-black">Connecting fragmented systems</p>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <input type="text" placeholder="Your Name" className={`w-full bg-slate-950 border ${errors.name ? 'border-red-500/50' : 'border-slate-800'} rounded-[1.5rem] px-8 py-5 text-white focus:outline-none focus:border-teal-500 transition-all font-bold placeholder:text-slate-700`} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                      {errors.name && <p className="text-[10px] text-red-500 mt-2 ml-4 font-black uppercase tracking-widest">{errors.name}</p>}
                    </div>
                    <div>
                      <input type="email" placeholder="Corporate Email" className={`w-full bg-slate-950 border ${errors.email ? 'border-red-500/50' : 'border-slate-800'} rounded-[1.5rem] px-8 py-5 text-white focus:outline-none focus:border-teal-500 transition-all font-bold placeholder:text-slate-700`} value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                      {errors.email && <p className="text-[10px] text-red-500 mt-2 ml-4 font-black uppercase tracking-widest">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input type="tel" placeholder="Phone" className="w-full bg-slate-950 border border-slate-800 rounded-[1.5rem] px-8 py-5 text-white focus:outline-none focus:border-teal-500 font-bold placeholder:text-slate-700" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                    <select className="w-full bg-slate-950 border border-slate-800 rounded-[1.5rem] px-8 py-5 text-white focus:outline-none focus:border-teal-500 font-bold appearance-none cursor-pointer" value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})}>
                      <option value="" className="text-slate-400">Inquiry Type</option>
                      <option value="investment">Capital Investment</option>
                      <option value="partnership">Strategic Partnership</option>
                      <option value="enterprise">Enterprise Integration</option>
                    </select>
                  </div>
                  <div>
                    <textarea rows="4" placeholder="Describe your vision... (min 20 chars)" className={`w-full bg-slate-950 border ${errors.message ? 'border-red-500/50' : 'border-slate-800'} rounded-[2rem] px-8 py-6 text-white focus:outline-none focus:border-teal-500 transition-all font-bold resize-none placeholder:text-slate-700`} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})}></textarea>
                    {errors.message && <p className="text-[10px] text-red-500 mt-1 ml-4 font-black uppercase tracking-widest">{errors.message}</p>}
                  </div>
                  
                  <button type="submit" disabled={formStatus === 'submitting'} className="w-full py-6 bg-teal-500 hover:bg-teal-400 text-[#020617] font-black rounded-[1.5rem] transition-all flex items-center justify-center gap-4 active:scale-[0.98] disabled:opacity-50 uppercase tracking-widest text-sm shadow-2xl shadow-teal-500/20">
                    {formStatus === 'submitting' ? <Loader2 className="animate-spin" /> : <>Send Inquiry <Send size={20} /></>}
                  </button>
                  <p className="text-center text-[10px] text-slate-600 font-bold tracking-widest uppercase">Target: info@medhaxtech.com</p>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-24 border-t border-slate-900 bg-[#01040a] text-center">
        <div className="max-w-7xl mx-auto px-6">
          <img src={logoBase64} alt="Medhax Technologies" className="h-10 mx-auto mb-14 opacity-80 brightness-110" />
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.6em] mb-6">
            Solving fragmented problems through vertical intelligence.
          </p>
          <div className="h-px w-20 bg-slate-800 mx-auto mb-10"></div>
          <p className="text-slate-700 text-[9px] font-bold uppercase tracking-[0.3em]">
            © 2024 MEDHAX TECHNOLOGIES PRIVATE LIMITED. ALL RIGHTS RESERVED.
          </p>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        body { font-family: 'Plus Jakarta Sans', sans-serif; }
        html { scroll-behavior: smooth; }
        .perspective-1000 { perspective: 1000px; }
        @keyframes fade-in { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-in { animation: fade-in 1.2s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes zoom-in { from { opacity: 0; transform: scale(0.9) translateY(40px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .zoom-in { animation: zoom-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes slide-in-from-left { from { transform: translateX(-60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .slide-in-from-left { animation: slide-in-from-left 1.2s cubic-bezier(0.16, 1, 0.3, 1) both; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #020617; }
        ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #14b8a6; }
      `}</style>
    </div>
  );
};

export default App;
